import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import axios from "axios";
import AddSessionNote from "./AddSessionNote"; // Adjust the import path as needed
import { BASE_API_URL } from "../../constants";
import { useSnackbar } from "../../Providers/SnackbarProvider"; // Adjust the import path as needed
import TextareaAutosize from "react-textarea-autosize";
import { xorDecrypt } from "../../encrypt";
import { handleSubmit } from "./GenerateSessionNote"; // Import the handleSubmit function
import axiosInstance from "../../api/axiosInstance";
import CircularProgress from "@mui/material/CircularProgress";
import { GenerateLoadingIndicator } from "../../Components/Loader";
import { useNotification } from "../../Components/CreditNotification"; // Import the useNotification hook

const parseText = (text) => {
  const lines = text.split("\n");
  const parsed = [];
  let currentHeading = null;
  let currentContent = [];

  const isHeading = (line) => {
    const headings = [
      "Reinforcers Used During Session",
      "Primary Target Areas Addressed",
      "Primary Target Areas Observed Addressed:",
      "Parent/Caregiver Training Goals Addressed:",
      "Parent/Caregiver Responses:",
      "Progress/Lack of Progress:",
      "Description of Maladaptive Behavior",
      "Session Summary",
      "Session Summary:",
      "Treatment Plan Summary:",
      "Progress Toward Goals:",
      "Modifications/Accommodations:",
      "Client’s Mood Response to Intervention",
      "Plan for next visit",
      "Summary of Assessment/Reassessment Session:",
      "Supervision Session Summary:",
      "Feedback to RBT/BT:",
      "Next Session Plans:",
      "Plan:",
    ];
    return headings.includes(line.trim()) || line.startsWith("###");
  };

  lines.forEach((line) => {
    line = line.replace(/\*\*/g, ""); // Remove any ** from the line
    if (isHeading(line)) {
      if (currentHeading) {
        parsed.push({
          type: "heading",
          content: currentHeading,
          text: currentContent.join("\n"),
        });
      }
      currentHeading = line.startsWith("###")
        ? line.replace("###", "").trim()
        : line.trim();
      currentContent = [];
    } else {
      currentContent.push(line.trim());
    }
  });

  if (currentHeading) {
    parsed.push({
      type: "heading",
      content: currentHeading,
      text: currentContent.join("\n"),
    });
  } else if (currentContent.length > 0) {
    parsed.push({
      type: "heading",
      content: "Other",
      text: currentContent.join("\n"),
    });
  }

  return parsed;
};

const RenderParsedText = ({ parsedText, handleChange }) =>
  parsedText.map((item, index) => (
    <div key={index}>
      {item.type === "heading" ? (
        <>
          <h3 style={{ margin: "20px 0px 0px 20px", color: "#e9a132" }}>
            {item.content}
          </h3>
          <TextareaAutosize
            style={{
              width: "98%",
              padding: "10px",
              fontSize: "16px",
              margin: "20px",
              backgroundColor: "#333",
              color: "#fff",
              border: "1px solid #e9a132",
              borderRadius: "4px",
            }}
            value={item.text}
            onChange={(e) => handleChange(index, e.target.value)}
          />
        </>
      ) : (
        <TextareaAutosize
          style={{
            width: "98%",
            padding: "10px",
            fontSize: "16px",
            margin: "20px",
            backgroundColor: "#333",
            color: "#fff",
            border: "1px solid #e9a132",
            borderRadius: "4px",
          }}
          value={item.text}
          onChange={(e) => handleChange(index, e.target.value)}
        />
      )}
    </div>
  ));

const replaceEmptyWithDefault = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : " ";
  }
  if (typeof value === "string") {
    return value.trim() || " ";
  }
  return value ?? " ";
};

const getCurrentTimeInUTC = () => {
  const now = new Date();
  return now.toISOString().split("T")[1].split(".")[0]; // Extract "HH:MM:SS" in UTC
};

const generatePayload = (procedureCode, initialValues) => {
  const mapToIds = (items) => {
    //console.log("Mapping items:", items); // Debugging line
    return Array.isArray(items) ? items.map((item) => item.id) : [];
  };

  const commonFields = {
    procedureCode: replaceEmptyWithDefault(procedureCode),
    clientId: initialValues.clientId,
    sessionType: replaceEmptyWithDefault(initialValues.sessionType),
    sessionParticipants: replaceEmptyWithDefault(initialValues.sessionParticipants),
    otherSessionParticipants: replaceEmptyWithDefault(initialValues.otherSessionParticipants),
    parentOrGurdianName: replaceEmptyWithDefault(initialValues.parentOrGurdianName),
    sessionStatus: replaceEmptyWithDefault(initialValues.sessionStatus),
    insuranceName: replaceEmptyWithDefault(initialValues.insuranceName),
    policyNumber: replaceEmptyWithDefault(initialValues.policyNumber),
    lastUpdatedBy: replaceEmptyWithDefault(initialValues.lastUpdatedBy),
    lastUpdatedTime: replaceEmptyWithDefault(initialValues.lastUpdatedTime),
    enteredTime: replaceEmptyWithDefault(initialValues.enteredTime),
    enteredBy: replaceEmptyWithDefault(initialValues.enteredBy),
    noteContent: replaceEmptyWithDefault(initialValues.noteContent),
    tempelate: replaceEmptyWithDefault(initialValues.tempelate),
    placeOfServiceCode: replaceEmptyWithDefault(initialValues.placeOfServiceCode),
    otherplaceOfService: replaceEmptyWithDefault(initialValues.otherplaceOfService),
  };

  let payload;

  switch (procedureCode) {
    case "97153":
      payload = {
        ...commonFields,
        relevanceFactor: replaceEmptyWithDefault(initialValues.relevanceFactor),
        rbtWorkStyle: replaceEmptyWithDefault(initialValues.rbtWorkStyle),
        otherRbtWorkStyle: replaceEmptyWithDefault(initialValues.otherRbtWorkStyle),
        skillStrategies: mapToIds(initialValues.skillStrategies),
        otherSkillStrategiesApplied: replaceEmptyWithDefault(initialValues.otherSkillStrategiesApplied),
        behaviorStrategiesApplied: mapToIds(initialValues.behaviorStrategiesApplied),
        otherBehaviorStrategiesApplied: replaceEmptyWithDefault(initialValues.otherBehaviorStrategiesApplied),
        malAdaptiveBehaviors: mapToIds(initialValues.malAdaptiveBehaviors),
        reinforcers: mapToIds(initialValues.reinforcers),
        primaryTargetAreasAddressed: mapToIds(initialValues.primaryTargetAreasAddressed),
        clientMoodResponse: mapToIds(initialValues.clientMoodResponse),
        nextSessionPlans: mapToIds(initialValues.nextSessionPlans),
      };
      break;

    case "97155":
      payload = {
        ...commonFields,
        relevanceFactor: replaceEmptyWithDefault(initialValues.relevanceFactor),
        nextSessionPlans: mapToIds(initialValues.nextSessionPlans),
        primaryTargetAreasAddressed: mapToIds(initialValues.primaryTargetAreasAddressed),
        behaviorTechName: replaceEmptyWithDefault(initialValues.behaviorTechName),
        feedbackRBT: replaceEmptyWithDefault(initialValues.feedbackRBT),
        supervisionProvided: replaceEmptyWithDefault(initialValues.supervisionProvided),
        otherSupervisionProvided: replaceEmptyWithDefault(initialValues.otherSupervisionProvided),
      };
      break;

    case "97156":
      payload = {
        ...commonFields,
        trainingGoalAddressed: replaceEmptyWithDefault(initialValues.trainingGoalAddressed),
        parentResponse: replaceEmptyWithDefault(initialValues.parentResponse),
        parentOrCaregiverTitle: replaceEmptyWithDefault(initialValues.parentOrCaregiverTitle),
        barriersDuringSession: replaceEmptyWithDefault(initialValues.barriersDuringSession),
        trainingProvidedBy: replaceEmptyWithDefault(initialValues.trainingProvidedBy),
        otherTrainingProvided: replaceEmptyWithDefault(initialValues.otherTrainingProvided),
        bcbaStrategiesApplied: mapToIds(initialValues.bcbaStrategiesApplied),
        otherBcbaStrategiesApplied: replaceEmptyWithDefault(initialValues.otherBcbaStrategiesApplied),
        bcbaActivitiesPerformed: replaceEmptyWithDefault(initialValues.bcbaActivitiesPerformed),
        nextSessionPlans: mapToIds(initialValues.nextSessionPlans),
      };
      break;

    case "97151":
      payload = {
        ...commonFields,
        plan: replaceEmptyWithDefault(initialValues.plan),
        activitiesPerformed: replaceEmptyWithDefault(initialValues.activitiesPerformed),
        relevanceFactor: replaceEmptyWithDefault(initialValues.relevanceFactor),
      };
      break;

    case "H0032":
      payload = {
        ...commonFields,
        progressTowardGoals: replaceEmptyWithDefault(initialValues.progressTowardGoals),
        modificationsAccommodations: replaceEmptyWithDefault(initialValues.modificationsAccommodations),
        nameOfPlanner: replaceEmptyWithDefault(initialValues.nameOfPlanner),
        sessionPurpose: replaceEmptyWithDefault(initialValues.sessionPurpose),
        otherSessionPurpose: replaceEmptyWithDefault(initialValues.otherSessionPurpose),
        treatmentPlanSummary: replaceEmptyWithDefault(initialValues.treatmentPlanSummary),
      };
      break;

    default:
      throw new Error("Unsupported procedure code");
  }

  return payload;
};

const SessionNoteResponse = ({
  open,
  onClose,
  InitialValues,
  clients,
  responseData,
}) => {
  const { showNotification } = useNotification();
  const { openSnackbar } = useSnackbar();
  const sharedLink = BASE_API_URL;
  const [text, setText] = useState(responseData);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [sessionDetails, setSessionDetails] = useState(() => {
    const currentDate = new Date();
    return {
      sessionStartTime: currentDate,
      sessionEndTime: new Date(currentDate.getTime() + 6 * 60 * 60 * 1000), // 6 hours later

      sessionDate: currentDate,
      modifier: ["97155", "97156", "97151"].includes(InitialValues.procedureCode) ? "HO" : "HN",
      diagnosisCode1: "F84.1",
      otherDiagnosisCode: "",
    };
  });

  useEffect(() => {
    if (open) {
      setText(responseData);
    }
  }, [open, responseData]);

  const handleAdd = (payload) => {
    openSnackbar("Saving..", "info");
    //console.log("Payload to be sent:", JSON.stringify(payload, null, 2));

    axiosInstance
      .post(`${sharedLink}/addsessionnote`, payload, {})
      .then((response) => {
        if (response.status === 200) {
          openSnackbar("Saved Successfully!", "success");
          setDetailsOpen(false);
        } else {
          openSnackbar("Failed to generate session notes", "error");
        }
      })
      .catch((err) => {
        console.error("Error during API call:", err); // Log the full error
        if (err.response) {
          console.error("Response error:", err.response.data);
        }
        openSnackbar(err, "error");
      });
  };

  const handleSave = () => {
    setConfirmationOpen(true);
  };
  const handleConfirmSave = () => {
    setConfirmationOpen(false);
    setDetailsOpen(true);
  };
  const handleCancelSave = () => {
    setConfirmationOpen(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "notes.txt";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const handleDetailsSave = (details) => {
    let subscriberId = xorDecrypt(localStorage.getItem("subscriberId"));
    subscriberId = subscriberId ? parseInt(subscriberId, 10) : 0; // Convert to integer and handle null/undefined

    let noteContent = text;
    noteContent = noteContent.replaceAll(/\\"\\\[/g, "(");
    noteContent = noteContent.replaceAll(/\\]\\"/g, "]");
    noteContent = noteContent.replace(/\\"/g, "\"");
    noteContent = noteContent.replace(/"/g, "");
    noteContent = noteContent.replace(/\*\*/g, "");


    const data = {
      subscriberId,
      ...details,
      lastUpdatedBy: "Admin",
      enteredBy: "Admin",
      noteContent,
      procedureCode: InitialValues?.procedureCode || "",
    };

    //console.log("Initial Values before payload generation:", InitialValues);
    const payload = InitialValues;
    //console.log("Payload after generatePayload:", payload);
    Object.assign(payload, data);
    //console.log("Final payload before sending:", payload);
    handleAdd(payload);
  };

  const handleGoBack = () => {
    setDetailsOpen(false);
  };

  const handleParsedTextChange = (index, newValue) => {
    const parsedText = parseText(text || "");
    parsedText[index].text = newValue;
    const updatedText = parsedText
      .map((item) => `### ${item.content}\n${item.text}`)
      .join("\n");
    setText(updatedText);
  };

  const handleRegenerate = async () => {
    const values = {
      procedureCode: InitialValues?.procedureCode || "",
      placeOfServiceCode: InitialValues?.placeOfServiceCode || "", 
      ...InitialValues,
    };
    await handleSubmit(null, values, openSnackbar, setText, null, setLoading);

    showNotification(" -1 Credit");
  };

  return (
    <>
      <Dialog
        open={open && !detailsOpen}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            marginTop: "7vh",
            backgroundColor: "#000", // Set dialog background color to black
            boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)", // White drop shadow
            borderRadius: "10px", // Rounded corners
            overflow: "hidden", // Hide scrollbars in the dialog
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>
          {" "}
          {/* Set title text color to white */}
          Generated Fields
          <IconButton
            edge="end"
            color="black"
            onClick={onClose}
            aria-label="close"
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon sx={{ color: "#fff" }} /> {/* Set icon color to white */}
          </IconButton>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleDownload}
            aria-label="download"
            sx={{ position: "absolute", right: 48, top: 8 }}
          >
            <FileDownloadIcon sx={{ color: "#fff" }} />{" "}
            {/* Set icon color to white */}
          </IconButton>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCopy}
            aria-label="copy"
            sx={{ position: "absolute", right: 88, top: 8 }}
          >
            <FileCopyIcon sx={{ color: "#fff" }} />{" "}
            {/* Set icon color to white */}
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            width: "100%", // Set content width to 100%
            padding: 0,
            overflowX: "hidden", // Hide horizontal scrollbar
            overflowY: "auto", // Enable vertical scrollbar
            backgroundColor: "#000", // Set content background color to black
            // Custom scrollbar styles
            "&::-webkit-scrollbar": {
              width: "8px", // Width of the scrollbar
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e9a132", // Scrollbar thumb color (orange)
              borderRadius: "8px", // Rounded corners for the scrollbar thumb
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#d0892f", // Darken the thumb on hover
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#333", // Scrollbar track color
              borderRadius: "8px", // Rounded corners for the scrollbar track
            },
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
              <GenerateLoadingIndicator />
            </Box>
          ) : (
            <div>
              <RenderParsedText
                parsedText={parseText(text)}
                handleChange={handleParsedTextChange}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "97%",
          }}
        >
          <Button
            onClick={handleRegenerate} // Call handleRegenerate on button click
            color="primary"
            variant="contained"
            startIcon={<RefreshIcon />}
            fullWidth
            sx={{
              backgroundColor: "#e9a132", // Set button background color to orange
              color: "#fff", // Set button text color to white
              "&:hover": {
                backgroundColor: "#d0892f", // Darken the background on hover
              },
            }}
          >
            Regenerate
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            startIcon={<NavigateNextIcon />}
            fullWidth
            sx={{
              backgroundColor: "#e9a132", // Set button background color to orange
              color: "#fff", // Set button text color to white
              "&:hover": {
                backgroundColor: "#d0892f", // Darken the background on hover
              },
            }}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmationOpen} onClose={handleCancelSave} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#000",
            color: "#fff",
            borderRadius: "10px",
            textAlign: "center",
            boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
          },
        }}>
        <DialogTitle sx={{ fontSize: "20px", fontWeight: "bold", color: "#e9a132" }}>
          Please Review Before Proceeding
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#000", padding: "20px" }}>
          <p style={{ fontSize: "16px", color: "#ddd" }}>
            The session note is AI-generated. Please review it carefully before proceeding.
          </p>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#000", justifyContent: "center" }}>
          <Button onClick={handleCancelSave} sx={{ backgroundColor: "#333", color: "#fff", "&:hover": { backgroundColor: "#444" } }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} sx={{ backgroundColor: "#e9a132", color: "#fff", "&:hover": { backgroundColor: "#d0892f" } }}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <AddSessionNote
        open={detailsOpen}
        onClose={handleGoBack}
        onSave={handleDetailsSave}
        initialValues={sessionDetails}
        clients={clients}
      />
    </>
  );
};

export default SessionNoteResponse;